<script>
import {
  firmwareUpdateState,
  installFirmwareRelease,
  LoadFirmware,
  resolveFirmwareRelease
} from "@/assets/js/LoadFirmware";
import {BIOTRON_DIAGNOSTICS_LOCK_NAME} from "@/assets/js/BiotronSafeInspector";

export default {
  emits: ['firmware_update_requested'],
  data() {
    return {
      isOnline: navigator.onLine,
      updateError: '',
      release: null,
      releaseLoading: false
    }
  },
  computed: {
    updateState() {
      if (!this.versionAware || !this.release) return 'unchecked'
      return firmwareUpdateState(this.currentVersion, this.release.version)
    },
    canUpdate() {
      if (this.disabled) return false
      if (!this.isOnline) return false
      if (!this.versionAware) return true
      return Boolean(this.device && this.release && this.updateState === 'update-available')
    }
  },
  mounted() {
    window.addEventListener('online', this.syncOnlineStatus)
    window.addEventListener('offline', this.syncOnlineStatus)
  },
  beforeUnmount() {
    window.removeEventListener('online', this.syncOnlineStatus)
    window.removeEventListener('offline', this.syncOnlineStatus)
  },
  methods: {
    syncOnlineStatus() {
      this.isOnline = navigator.onLine
      if (this.isOnline) this.updateError = ''
    },
    async checkFirmware() {
      if (!this.versionAware || !this.isOnline || this.releaseLoading || this.release) return
      this.updateError = ''
      this.releaseLoading = true
      try {
        this.release = await resolveFirmwareRelease(this.repo)
        if (!this.release.version) throw new Error('Published firmware version could not be verified.')
      } catch (error) {
        this.release = null
        this.updateError = error.message
      } finally {
        this.releaseLoading = false
      }
    },
    async updateFirmware() {
      this.updateError = ''
      try {
        if (this.versionAware) {
          await this.checkFirmware()
          if (!this.canUpdate) throw new Error('Automatic update is paused for this firmware state.')
          const install = () => {
            this.$emit('firmware_update_requested')
            installFirmwareRelease(this.release, this.device, this.repo)
          }
          if (navigator.locks && typeof navigator.locks.request === 'function') {
            let started = false
            await navigator.locks.request(BIOTRON_DIAGNOSTICS_LOCK_NAME, {ifAvailable: true}, lock => {
              if (!lock) return
              started = true
              install()
            })
            if (!started) throw new Error('A Biotron diagnostic is active in another tab. Close it before updating firmware.')
          } else {
            install()
          }
        } else {
          await LoadFirmware(this.repo, this.device)
        }
      } catch (error) {
        this.updateError = error.message
      }
    }
  },

  props: {
      repo: {
        type: String
      },
      text: {
        type: String,
        default: "Update Firmware",
      },
      device: Object,
      currentVersion: String,
      versionAware: {
        type: Boolean,
        default: false
      },
      helpUrl: {
        type: String,
        default: 'https://help.playtronica.com/troubleshooting/firmware-reset/#biotron-recovery'
      },
      disabled: {
        type: Boolean,
        default: false
      }
  }
}
</script>

<template>
  <button data-bs-toggle="modal" data-bs-target="#UpdateConf" class="btn btn-primary"
          :class="$attrs.class" :disabled="disabled" @click="checkFirmware">{{text}}</button>

  <div class="modal fade" id="UpdateConf" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="exampleModalLabel">{{ versionAware ? "Check Biotron firmware" : "Update Firmware" }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <template v-if="!versionAware || updateState === 'update-available'">
            <p>
              This downloads a <code>.uf2</code> firmware file and restarts Biotron as a USB drive.
              When the drive appears, move the downloaded file onto it. Biotron will restart when copying finishes.
            </p>
            <p class="alert alert-warning">Do not start unless you can finish the copy. Biotron will not play while it is waiting for the file.</p>
          </template>
          <p v-if="!isOnline" class="alert alert-warning mb-0" role="status">
            Firmware updates require an internet connection. Device settings remain available offline.
          </p>
          <p v-else-if="versionAware && !device" class="alert alert-secondary mb-0" role="status">
            Connect Biotron to check whether an update is needed.
          </p>
          <p v-else-if="releaseLoading" class="alert alert-secondary mb-0" role="status">
            Checking the published firmware…
          </p>
          <p v-else-if="updateState === 'unknown-current'" class="alert alert-warning mb-0" role="status">
            Settings could not identify the firmware currently inside Biotron, so the update is blocked.
            Follow the <a :href="helpUrl" target="_blank" rel="noopener">step-by-step firmware instructions</a>.
          </p>
          <p v-else-if="updateState === 'update-available'" class="alert alert-warning mb-0" role="status">
            An update is available: {{ currentVersion }} → {{ release.version }}. Save a browser preset first because this update may reset settings inside Biotron.
          </p>
          <p v-else-if="updateState === 'up-to-date'" class="alert alert-success mb-0" role="status">
            Biotron already has the current published firmware: {{ currentVersion }}.
          </p>
          <p v-else-if="updateState === 'newer-than-published'" class="alert alert-success mb-0" role="status">
            This Biotron has firmware {{ currentVersion }}, which is newer than the published {{ release.version }}. Settings will not replace it with an older version.
          </p>
          <p v-if="updateError" class="alert alert-danger mb-0" role="alert">{{ updateError }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" :disabled="!canUpdate"
                  @click="updateFirmware">
            {{ versionAware ? "Download firmware and restart Biotron" : "Update" }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
